// What a beaten Monarch pays: a trophy (§31 A2) offering `RULES.packSize`
// cards off `MONARCH_PACK`, rolled when it is earned (`payForMonarchs`,
// `deck.ts`). The player exchanges it for one, or keeps it for score.
//
// Unpredictable to a player, identical on a replay: the roll is `streamFor`,
// a pure function of the seed, how far in the game is and a salt. Never
// `Math.random`.

import { RULES } from "../rules";
import type { CardId } from "./cards";

/**
 * The pack table. Weights sum to 100, so a weight *is* its share of every
 * card dealt — and `rarity.ts` reads rarity off it rather than a second table
 * that would drift.
 */
export const MONARCH_PACK: readonly { card: CardId; weight: number }[] = [
  // The commonest, and they pay for every card added.
  { card: "full-production-gambit", weight: 11 },
  { card: "reactor-boosters", weight: 12 },
  // The ordinary cards.
  { card: "hydraulic-clasp", weight: 7 },
  { card: "iff-spoofer", weight: 7 },
  { card: "stasis-field", weight: 7 },
  { card: "bridges", weight: 7 },
  { card: "signet-crown", weight: 7 },
  { card: "yearning-for-the-mines", weight: 7 },
  // §33 T5, paid for by the two commonest.
  { card: "march", weight: 7 },
  { card: "programmer-oil", weight: 6 },
  { card: "the-enemy-is-at-the-gates", weight: 4 },
  // The ones that open a way of playing.
  { card: "coronation", weight: 4 },
  { card: "spatial-manifold", weight: 4 },
  { card: "static-moat", weight: 4 },
  // The rare ones: the royal prerogatives that take an exchange apart.
  { card: "micro-singularity-bomb", weight: 3 },
  { card: "time-fluxxer", weight: 3 }
];

/** One weighted pick. One draw always, so the stream never depends on the table. */
function pickCard(pick: number): CardId {
  const total = MONARCH_PACK.reduce((sum, row) => sum + row.weight, 0);
  let cut = pick * total;
  for (const row of MONARCH_PACK) {
    cut -= row.weight;
    if (cut < 0) return row.card;
  }
  // A rounding edge only.
  return MONARCH_PACK[MONARCH_PACK.length - 1].card;
}

/** A Monarch's pack: `RULES.packSize` picks, one draw each. */
export function rollPack(roll: () => number): CardId[] {
  const out: CardId[] = [];
  for (let i = 0; i < RULES.packSize; i++) out.push(pickCard(roll()));
  return out;
}

/** Each card's share of every pack, as a percentage. */
export function packShares(): Map<CardId, number> {
  const total = MONARCH_PACK.reduce((sum, row) => sum + row.weight, 0);
  return new Map(MONARCH_PACK.map((row) => [row.card, (row.weight / total) * 100]));
}
